/**
 * Reliability Prediction Engine
 * OAV-036: Multi-standard failure rate prediction (MIL-217F, 217Plus, Telcordia, FIDES)
 *
 * REFERENCES:
 *   - MIL-HDBK-217F: Reliability Prediction of Electronic Equipment (1991)
 *   - Telcordia SR-332 Issue 4: Reliability Prediction Procedure for Electronic Equipment (2011)
 *   - FIDES Guide 2009: Reliability Methodology for Electronic Systems (2009)
 *   - Quanterion Solutions: ReliaSSist 217Plus™ (2006 update to MIL-217F)
 *
 * OVERVIEW:
 *   This module provides failure rate prediction across four standard approaches:
 *   - MIL-HDBK-217F (legacy, parts-count and part-stress methods)
 *   - 217Plus (updated π factors and base rates)
 *   - Telcordia SR-332 Issue 4 (method I: parts-count)
 *   - FIDES Guide 2009 (physical + process + environmental contributions)
 *
 *   Typical usage: Create engine, call predict() or partsCountAnalysis() with BOM.
 */

class ReliabilityPredictionEngine {
  /**
   * BASE FAILURE RATES (λ_b) from published standards
   * Units: failures per million hours (F/MH) at reference conditions
   */
  static MIL217F_BASE_RATES = {
    'ceramic-capacitor': 0.0012,
    'aluminum-capacitor': 0.0058,
    'tantalum-capacitor': 0.0012,
    'film-capacitor': 0.0008,
    'resistor-film': 0.0001,
    'resistor-wirewound': 0.00008,
    'resistor-thick-film': 0.00012,
    'diode-silicon': 0.00018,
    'diode-zener': 0.00022,
    'transistor-bjt': 0.00048,
    'transistor-fet': 0.00042,
    'ic-cmos': 0.00085,
    'ic-ttl': 0.00095,
    'ic-analog': 0.0012,
    'transformer': 0.00025,
    'inductor': 0.00015,
    'connector': 0.00095,
    'switch': 0.0015,
    'relay': 0.0032,
    'fuse': 0.0001
  };

  static TELCORDIA_BASE_RATES = {
    'ceramic-capacitor': 0.0025,
    'aluminum-capacitor': 0.0092,
    'tantalum-capacitor': 0.0035,
    'resistor': 0.00012,
    'diode': 0.00025,
    'transistor-bjt': 0.00065,
    'transistor-fet': 0.00048,
    'ic-digital': 0.0011,
    'ic-analog': 0.0015,
    'inductor': 0.00018,
    'transformer': 0.00035,
    'connector': 0.0012,
    'switch': 0.0018,
    'relay': 0.0038
  };

  static FIDES_BASE_RATES = {
    'ceramic-capacitor': 0.0015,
    'aluminum-capacitor': 0.0070,
    'tantalum-capacitor': 0.0020,
    'film-capacitor': 0.0010,
    'resistor': 0.00010,
    'diode': 0.00020,
    'transistor': 0.00045,
    'ic-digital': 0.00088,
    'ic-analog': 0.0011,
    'transformer': 0.00028,
    'connector': 0.0010,
    'relay': 0.0035
  };

  constructor() {
    this.standardTables = {
      'MIL-HDBK-217F': ReliabilityPredictionEngine.MIL217F_BASE_RATES,
      '217Plus': ReliabilityPredictionEngine.MIL217F_BASE_RATES,
      'Telcordia': ReliabilityPredictionEngine.TELCORDIA_BASE_RATES,
      'FIDES': ReliabilityPredictionEngine.FIDES_BASE_RATES
    };
  }

  /**
   * Predict failure rate for a single component
   *
   * @param {object} component
   *   - type: part type (e.g., 'ceramic-capacitor', 'resistor', 'ic-cmos')
   *   - quantity: number of identical parts (default 1)
   *   - temperature: Operating temperature (°C) - used for π_T adjustment
   *   - qualityLevel: Quality/reliability level (S, B, M, T) - used for π_Q
   * @param {string} standard - One of: 'MIL-HDBK-217F', '217Plus', 'Telcordia', 'FIDES'
   * @returns {object} {type, standard, lambda_b, pi_factors, lambda_p, mtbf}
   */
  predict(component, standard = 'MIL-HDBK-217F') {
    if (!this.standardTables[standard]) {
      throw new Error(`Unknown standard: ${standard}`);
    }

    const baseTable = this.standardTables[standard];
    const lambda_b = baseTable[component.type];

    if (!lambda_b) {
      throw new Error(`No data for part type: ${component.type}`);
    }

    // Compute π factors (environmental, quality, temperature, etc.)
    const piFactors = this._computePiFactors(component, standard);

    // Failure rate: λ_p = π_Q · π_E · λ_b · π_T
    const lambda_p = piFactors.pi_Q * piFactors.pi_E * lambda_b * piFactors.pi_T;

    // MTBF in hours: MTBF = 1 / (λ_p × 1e-6)
    const mtbf = lambda_p > 0 ? 1e6 / lambda_p : Infinity;

    return {
      type: component.type,
      standard,
      lambda_b,
      piFactors,
      lambda_p,  // failures per million hours
      mtbfHours: mtbf,
      mtbfYears: mtbf / 8760
    };
  }

  /**
   * Compute π (pi) environmental and quality adjustment factors
   * Simplified per MIL-217F and variants
   *
   * @param {object} component
   * @param {string} standard
   * @returns {object} {pi_Q, pi_E, pi_T, pi_total}
   */
  _computePiFactors(component, standard) {
    // Quality factor (π_Q): S=0.5, B=1.0, M=2.0, T=5.5 for MIL-217F
    const qualityMap = { S: 0.5, B: 1.0, M: 2.0, T: 5.5 };
    const quality = component.qualityLevel || 'M';
    const pi_Q = qualityMap[quality] || 2.0;

    // Environmental factor (π_E): simplification across 12 environments
    // Ground fixed: 1.0, Mobile: 2.0, Naval: 3.0, Airborne: 4.0, etc.
    const environment = component.environment || 'Ground-Fixed';
    const environmentMap = {
      'Ground-Fixed': 1.0,
      'Ground-Mobile': 2.0,
      'Naval-Sheltered': 3.0,
      'Naval-Unsheltered': 4.0,
      'Airborne-Uninhabited': 5.0,
      'Airborne-Inhabited': 4.0,
      'Space': 0.5
    };
    const pi_E = environmentMap[environment] || 1.0;

    // Temperature factor (π_T): simplified Arrhenius-based
    // Reference temp = 40°C, activation energy ≈ 0.1 eV
    const tempC = component.temperature || 40;
    const pi_T =
      Math.exp(0.00134 * Math.pow(tempC - 40, 2)) || 1.0;

    // 217Plus updates: slightly lower multipliers
    if (standard === '217Plus') {
      return {
        pi_Q: pi_Q * 0.95,
        pi_E: pi_E * 0.98,
        pi_T: pi_T * 0.97
      };
    }

    return { pi_Q, pi_E, pi_T };
  }

  /**
   * Parts Count Analysis (Telcordia Method I, simplified)
   * λ_dev = Σ (N_i × λ_g_i × π_Q)
   *
   * @param {array} billOfMaterials
   *   Array of {type, quantity, qualityLevel, temperature, environment}
   * @param {string} standard - Prediction standard
   * @returns {object} {standard, components: [...], systemLambda, systemMtbf}
   */
  partsCountAnalysis(billOfMaterials, standard = 'MIL-HDBK-217F') {
    if (!Array.isArray(billOfMaterials) || billOfMaterials.length === 0) {
      throw new Error('billOfMaterials must be a non-empty array');
    }

    let systemLambda = 0;
    const components = [];

    for (const part of billOfMaterials) {
      const prediction = this.predict(part, standard);
      const componentLambda = (part.quantity || 1) * prediction.lambda_p;

      components.push({
        type: part.type,
        quantity: part.quantity || 1,
        lambda: prediction.lambda_p,
        totalLambda: componentLambda,
        mtbfYears: prediction.mtbfYears
      });

      systemLambda += componentLambda;
    }

    // System MTBF
    const systemMtbf = systemLambda > 0 ? 1e6 / systemLambda : Infinity;
    const systemMtbfYears = systemMtbf / 8760;

    return {
      standard,
      components,
      systemLambda,  // failures per million hours
      systemMtbfHours: systemMtbf,
      systemMtbfYears,
      analysisDate: new Date().toISOString()
    };
  }

  /**
   * FIDES Guide 2009 Simplified Calculation
   * λ_PHY = λ_ref · π_ProcessFamily · π_ProcessMature · π_OperatingPoint
   * λ_INDUCED = π_ProcessModuleNonConformance · π_SystemLevelNonConformance
   * λ = (λ_PHY + λ_INDUCED) · π_PM
   *
   * @param {object} component - With fides-specific fields
   * @returns {object} FIDES prediction result
   */
  fidesPredict(component) {
    const baseTable = this.standardTables['FIDES'];
    const lambda_ref = baseTable[component.type];

    if (!lambda_ref) {
      throw new Error(`No FIDES data for: ${component.type}`);
    }

    // Process family factor (CMOS vs TTL vs Analog)
    const procFamilyMap = {
      'CMOS': 1.0,
      'Bipolar': 1.2,
      'Analog': 1.3,
      'Mixed-Signal': 1.4
    };
    const pi_ProcessFamily = procFamilyMap[component.processFamily] || 1.0;

    // Process maturity (new=3.0, mature=0.8)
    const pi_ProcessMature = component.isNewProcess ? 3.0 : 0.8;

    // Operating point stress (voltage, frequency ratio)
    const pi_OperatingPoint = 1.0 + (component.stressRatio || 0.5);

    // Physical failure rate
    const lambda_PHY = lambda_ref * pi_ProcessFamily * pi_ProcessMature * pi_OperatingPoint;

    // Induced failures (process non-conformances)
    const pi_InducedNonConformance = component.hasDesignIssues ? 1.5 : 1.0;

    // Part Management factor (screening, handling, packaging)
    const pi_PM = component.qualityLevel === 'S' ? 0.5 : 1.0;

    const lambda_FIDES = lambda_PHY * pi_InducedNonConformance * pi_PM;

    return {
      type: component.type,
      standard: 'FIDES',
      lambda_ref,
      pi_ProcessFamily,
      pi_ProcessMature,
      pi_OperatingPoint,
      lambda_PHY,
      lambda_INDUCED: lambda_PHY * pi_InducedNonConformance,
      lambda_total: lambda_FIDES,
      mtbfHours: lambda_FIDES > 0 ? 1e6 / lambda_FIDES : Infinity
    };
  }

  /**
   * Return all available part types for a given standard
   * @param {string} standard
   * @returns {array} list of supported part types
   */
  getAvailablePartTypes(standard = 'MIL-HDBK-217F') {
    const table = this.standardTables[standard];
    if (!table) return [];
    return Object.keys(table);
  }

  /**
   * Compare predictions across multiple standards for the same component
   * @param {object} component
   * @returns {object} Comparison table
   */
  compareStandards(component) {
    const standards = ['MIL-HDBK-217F', '217Plus', 'Telcordia', 'FIDES'];
    const results = {};

    for (const std of standards) {
      try {
        const prediction = this.predict(component, std);
        results[std] = {
          lambda_p: prediction.lambda_p,
          mtbfYears: prediction.mtbfYears
        };
      } catch (e) {
        results[std] = { error: e.message };
      }
    }

    return {
      component: component.type,
      standardComparison: results,
      variance: this._computeVariance(results)
    };
  }

  /**
   * Compute coefficient of variation across standard predictions
   * @param {object} results
   * @returns {number} CV percentage
   */
  _computeVariance(results) {
    const validValues = Object.values(results)
      .filter(r => r.lambda_p)
      .map(r => r.lambda_p);

    if (validValues.length < 2) return 0;

    const mean = validValues.reduce((a, b) => a + b, 0) / validValues.length;
    const variance =
      validValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
      validValues.length;
    const stdDev = Math.sqrt(variance);

    return Math.round((stdDev / mean) * 100);
  }


  /**
   * OAV-054 (final): Full MIL-HDBK-217F part-stress walk.
   *
   * λ_part = λ_b · π_T · π_S · π_Q · π_E · π_A   (per §5 of the handbook)
   *
   * Each component supplies:
   *   { type, qualityLevel, environment, junctionTempC, electricalStressRatio,
   *     applicationFactor }
   *
   * Returns each component's lambda with itemised π factors so a BoM-driven
   * prediction attributes failure rate to its source.
   */
  partStressAnalysis(components, standard = 'MIL-HDBK-217F') {
    if (!Array.isArray(components) || components.length === 0) {
      throw new Error('partStressAnalysis: components must be a non-empty array');
    }
    const out = [];
    let systemLambda = 0;
    for (const comp of components) {
      const tbl = this.standardTables[standard] || {};
      const lambda_b = tbl[comp.type];
      if (lambda_b == null) {
        out.push({ type: comp.type, error: `No base rate for ${comp.type}` });
        continue;
      }
      const qualityMap = { S: 0.5, B: 1.0, M: 2.0, T: 5.5 };
      const pi_Q = qualityMap[comp.qualityLevel || 'M'] || 2.0;
      const envMap = {
        'Ground-Benign': 0.5, 'Ground-Fixed': 1.0, 'Ground-Mobile': 2.0,
        'Naval-Sheltered': 3.0, 'Naval-Unsheltered': 4.0,
        'Airborne-Inhabited': 4.0, 'Airborne-Uninhabited': 5.0,
        'Space': 0.5, 'Missile-Flight': 8.0
      };
      const pi_E = envMap[comp.environment || 'Ground-Fixed'] || 1.0;
      const tj = comp.junctionTempC != null ? comp.junctionTempC : (comp.temperature || 40);
      const k = 8.617333262e-5;     // eV/K
      const pi_T = Math.exp((-0.4 / k) * (1 / (tj + 273.15) - 1 / 298.15));
      const sRatio = Math.min(1, Math.max(0, comp.electricalStressRatio != null ? comp.electricalStressRatio : 0.5));
      const pi_S = Math.pow(sRatio, 2);
      const pi_A = comp.applicationFactor != null ? comp.applicationFactor : 1.0;

      const lambda_p = lambda_b * pi_T * pi_S * pi_Q * pi_E * pi_A;
      const totalLambda = (comp.quantity || 1) * lambda_p;
      systemLambda += totalLambda;
      out.push({
        type: comp.type, quantity: comp.quantity || 1,
        lambda_b, pi_T, pi_S, pi_Q, pi_E, pi_A,
        lambda_p, totalLambda,
        mtbfYears: lambda_p > 0 ? (1e6 / lambda_p) / 8760 : Infinity,
      });
    }
    return {
      standard,
      method: 'part-stress',
      components: out,
      systemLambda,
      systemMtbfHours: systemLambda > 0 ? 1e6 / systemLambda : Infinity,
      systemMtbfYears: systemLambda > 0 ? (1e6 / systemLambda) / 8760 : Infinity,
      analysisDate: new Date().toISOString(),
    };
  }
}

module.exports = ReliabilityPredictionEngine;
